import React from "react";
import "./TermsOfService.css";

const TermsOfService = () => {
  return (
    <div className="legal-page">
      {/* Hero Section */}
      <section className="legal-hero">
        <div className="container">
          <h1>Terms of Service</h1>
          <p className="legal-subtitle">
            Welcome to GoalFlex. These terms govern your use of our platform and
            services.
          </p>
          <div className="legal-meta">
            <span>
              <strong>Effective Date:</strong> January 1, 2025
            </span>
            <span>
              <strong>Last Updated:</strong> December 29, 2025
            </span>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="legal-content">
        <div className="container">
          <div className="legal-intro">
            <p>
              By accessing or using GoalFlex's website, mobile application, or
              services, you agree to be bound by these Terms of Service
              ("Terms"). Please read them carefully as they contain important
              information about your rights and obligations.
            </p>
          </div>

          <div className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By creating an account, downloading our app, or using our services
              in any way, you acknowledge that you have read, understood, and
              agree to be bound by these Terms and our Privacy Policy. If you do
              not agree to these Terms, you may not use our services.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Eligibility and Account Registration</h2>
            <div className="subsection">
              <h3>Age Requirements</h3>
              <ul>
                <li>You must be at least 16 years old to use GoalFlex</li>
                <li>Users under 18 must have parental consent</li>
                <li>
                  Minors' use is subject to additional restrictions for safety
                </li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Account Responsibilities</h3>
              <ul>
                <li>
                  Provide accurate and complete information during registration
                </li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>
                  You are responsible for all activities under your account
                </li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>3. Platform Usage and Conduct</h2>
            <div className="subsection">
              <h3>Permitted Uses</h3>
              <ul>
                <li>Finding and joining pickup soccer games</li>
                <li>Creating and organizing games</li>
                <li>Communicating with other players for game coordination</li>
                <li>Rating and reviewing your game experiences</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Prohibited Activities</h3>
              <ul>
                <li>Harassment, abuse, or discrimination of any kind</li>
                <li>Posting false, misleading, or inappropriate content</li>
                <li>
                  Using the platform for commercial purposes without
                  authorization
                </li>
                <li>
                  Attempting to hack, disrupt, or compromise platform security
                </li>
                <li>Creating fake accounts or impersonating others</li>
                <li>Violating any applicable laws or regulations</li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>4. Game Participation and Safety</h2>
            <ul>
              <li>
                <strong>Voluntary Participation:</strong> All game participation
                is voluntary and at your own risk
              </li>
              <li>
                <strong>Physical Fitness:</strong> Ensure you are physically
                capable of participating in soccer activities
              </li>
              <li>
                <strong>Safety Equipment:</strong> Use appropriate safety gear
                and follow venue rules
              </li>
              <li>
                <strong>Insurance:</strong> GoalFlex does not provide medical
                insurance coverage
              </li>
              <li>
                <strong>Emergency Contact:</strong> Maintain updated emergency
                contact information
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>5. Payments and Refunds</h2>
            <div className="subsection">
              <h3>Game Fees</h3>
              <ul>
                <li>
                  Some games may require payment for venue, equipment, or
                  organization costs
                </li>
                <li>
                  Payment is due at the time of booking unless otherwise
                  specified
                </li>
                <li>Prices are subject to change with reasonable notice</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Cancellation Policy</h3>
              <ul>
                <li>Free cancellation up to 24 hours before game time</li>
                <li>No refunds for late cancellations (within 24 hours)</li>
                <li>
                  No-shows may forfeit their payment and face account
                  restrictions
                </li>
                <li>Weather-related cancellations receive full refunds</li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>6. Content and Intellectual Property</h2>
            <ul>
              <li>
                <strong>User Content:</strong> You retain ownership of content
                you create, but grant us license to use it
              </li>
              <li>
                <strong>Platform Content:</strong> GoalFlex owns all platform
                design, software, and original content
              </li>
              <li>
                <strong>Trademarks:</strong> GoalFlex name, logo, and trademarks
                are our property
              </li>
              <li>
                <strong>Respect IP:</strong> Do not use others' content without
                permission
              </li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>7. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Our Privacy Policy, which is
              incorporated into these Terms, explains how we collect, use, and
              protect your information. By using our services, you consent to
              our data practices as described in our Privacy Policy.
            </p>
          </div>

          <div className="legal-section">
            <h2>8. Platform Availability and Modifications</h2>
            <ul>
              <li>
                We strive for 99.9% uptime but cannot guarantee uninterrupted
                service
              </li>
              <li>
                We may modify, suspend, or discontinue features with reasonable
                notice
              </li>
              <li>
                Maintenance and updates may temporarily affect availability
              </li>
              <li>We reserve the right to improve and update our services</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>9. Account Suspension and Termination</h2>
            <div className="subsection">
              <h3>Grounds for Action</h3>
              <ul>
                <li>Violation of these Terms of Service</li>
                <li>Fraudulent or suspicious activity</li>
                <li>Repeated no-shows or disruptive behavior</li>
                <li>Safety concerns or complaints from other users</li>
              </ul>
            </div>
            <div className="subsection">
              <h3>Process</h3>
              <ul>
                <li>We will typically provide warnings before suspension</li>
                <li>Serious violations may result in immediate termination</li>
                <li>You may appeal decisions through our support system</li>
                <li>Terminated users may not create new accounts</li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              <strong>IMPORTANT:</strong> You play entirely at your own risk.
              GoalFlex provides a platform to connect players but is not
              responsible for:
            </p>
            <ul>
              <li>
                Injuries, accidents, or any incidents occurring during games
              </li>
              <li>Lost property, stolen items, or equipment damage</li>
              <li>Actions or behavior of other users</li>
              <li>Venue conditions, equipment quality, or safety</li>
              <li>Disputes between players or organizers</li>
              <li>Any disruptions during the game</li>
            </ul>
            <p>
              Players are responsible for their own safety and equipment. Our
              liability is limited to the maximum extent permitted by law. In no
              event shall our total liability exceed the amount you paid to
              GoalFlex in the past 12 months.
            </p>
          </div>

          <div className="legal-section">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless GoalFlex, its officers,
              directors, employees, and agents from any claims, damages, or
              expenses arising from your use of the platform, violation of these
              Terms, or infringement of any rights.
            </p>
          </div>

          <div className="legal-section">
            <h2>12. Dispute Resolution</h2>
            <div className="subsection">
              <h3>Governing Law</h3>
              <p>
                These Terms are governed by the laws of California, United
                States.
              </p>
            </div>
            <div className="subsection">
              <h3>Resolution Process</h3>
              <ul>
                <li>
                  First, contact our support team to resolve issues informally
                </li>
                <li>
                  If needed, disputes will be resolved through binding
                  arbitration
                </li>
                <li>
                  Class action lawsuits are waived except where prohibited by
                  law
                </li>
                <li>
                  California courts have jurisdiction for any remaining disputes
                </li>
              </ul>
            </div>
          </div>

          <div className="legal-section">
            <h2>13. Changes to Terms</h2>
            <p>
              We may update these Terms periodically to reflect changes in our
              services or legal requirements. Material changes will be
              communicated via email or platform notification at least 30 days
              before taking effect. Continued use after changes constitutes
              acceptance of the updated Terms.
            </p>
          </div>

          <div className="legal-section">
            <h2>14. Contact Information</h2>
            <p>Questions about these Terms? We're here to help:</p>
            <div className="contact-info">
              <p>
                <strong>Legal Team:</strong>{" "}
                <a href="mailto:legal@goalflex.net">legal@goalflex.net</a>
              </p>
              <p>
                <strong>Support:</strong>{" "}
                <a href="mailto:support@goalflex.net">support@goalflex.net</a>
              </p>
              <p>
                <strong>Address:</strong> GoalFlex, Inc.
                <br />
                123 Soccer Street
                <br />
                San Francisco, CA 94105
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
